import { useMutation } from '@apollo/client';
import lodashGet from 'lodash/get';
import { useState } from 'react';
import { InputType } from 'sales-types-common';
import { createMinimalOrderMutationParsed, customerId, businessCustomerId, useDisableScroll } from '../../../../common';
import {
  CustomerType,
  IConfiguration,
  OrderCreationType,
  OrderSource,
  OrderType,
  isForeignBrandOrder,
} from 'next-common';
import { RadioButton, CustomModal, Button, InputField, Dropdown, IconType, LoadingSpinner } from '@repo/ui/components';

export interface ICreateOrder {
  onClose(): void;
  createOrder: boolean;
  configuration: IConfiguration;
}

const orderTypeList = [
  {
    label: OrderCreationType.CUSTOMER,
    value: OrderType.ORDER,
  },
  {
    label: OrderCreationType.DEMO,
    value: OrderType.DEMO,
  },
  {
    label: OrderCreationType.STOCK,
    value: OrderType.STOCK,
  },
  {
    label: OrderCreationType.USED,
    value: OrderType.USED,
  },
];

export const CreateOrder = (props: ICreateOrder) => {
  const [make, setMake] = useState<string>('');
  const [modelSeries, setModelSeries] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>(CustomerType.PRIVATE);

  const [selectedValue, setSelectedValue] = useState({
    label: OrderCreationType.CUSTOMER,
    value: '1',
  });

  const [createOrderMutation] = useMutation(createMinimalOrderMutationParsed);
  useDisableScroll(props?.createOrder);

  const createOrder = () => {
    setErrorMessage('');
    setIsLoading(true);
    const input = {
      customerId: selectedCustomerType === CustomerType.BUSINESS ? businessCustomerId : customerId,
      make: make ? make.toUpperCase() : '',
      modelSeries: modelSeries ?? '',
      quoteId: '',
      source: OrderSource[OrderSource.NEXT],
      orderType: selectedValue?.label || '',
    };
    createOrderMutation({ variables: { input } })
      .then((result) => {
        const orderURI = lodashGet(result, 'data.createMinimalOrder.orderURI');
        if (orderURI) {
          window.open(orderURI, '_blank');
          props.onClose();
        }
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
        setErrorMessage('Det oppsto en feil under oppretting av en bestilling');
      });
  };

  const onChangeMake = (input: string) => {
    setMake(input);
    setErrorMessage('');
  };

  const onChangeModelSeries = (input: string) => {
    setModelSeries(input);
    setErrorMessage('');
  };

  const onModalClose = () => {
    props.onClose();
  };

  const handleChange = (selectedOption) => {
    setSelectedValue(selectedOption);
  };

  const configuration = props?.configuration?.features;
  const toUpperCaseArray = (arr?: string[]): string[] => arr?.map((elem) => elem.toUpperCase()) || [];

  const stoppedDemoBrands: string[] = toUpperCaseArray(configuration?.stoppedDemoBrands);
  const stoppedStockBrands: string[] = toUpperCaseArray(configuration?.stoppedStockBrands);

  const dropDownOptions = () => {
    return orderTypeList?.filter((item) => {
      const makeUpper = make?.toUpperCase();
      return !(
        (stoppedDemoBrands?.length && stoppedDemoBrands.includes(makeUpper) && item.label === OrderCreationType.DEMO) ||
        (stoppedStockBrands?.length &&
          stoppedStockBrands.includes(makeUpper) &&
          item.label === OrderCreationType.STOCK) ||
        (!props?.configuration?.features?.usedCar && item.label === OrderCreationType.USED)
      );
    });
  };

  const isOfferBtnDisable = () => {
    const isUsedCar = selectedValue?.label === OrderType[OrderType.USED];
    if (isUsedCar) return false;
    if (!make) return true;
    else {
      const isForeignCar = isForeignBrandOrder(null, { make, orderType: selectedValue?.label?.toString() });
      if (isForeignCar) return false;
      if (!modelSeries) return true;
    }
  };

  return (
    <CustomModal onCloseClick={onModalClose} open={true} className="w-[40rem] bg-lightGrey">
      <div className="p-8">
        <header className="mb-8">
          <h2>Opprett ordre</h2>
        </header>
        <div className="p-6 flex flex-col gap-6">
          <div className="flex gap-6">
            <RadioButton
              id="privateCustomer"
              label={'Privat'}
              checked={selectedCustomerType === CustomerType.PRIVATE}
              onChange={() => setSelectedCustomerType(CustomerType.PRIVATE)}
              labelClassName="mt-0"
            />
            <RadioButton
              id="businessCustomer"
              label={'Bedrift'}
              checked={selectedCustomerType === CustomerType.BUSINESS}
              onChange={() => setSelectedCustomerType(CustomerType.BUSINESS)}
              labelClassName="mt-0"
            />
          </div>
          <InputField
            id="makeInput"
            className="border-2 border-concrete bg-pureWhite enabled:hover:bg-pureWhite text-lg w-full"
            labelClassName="text-charcoal"
            label="Merke"
            type={InputType.TEXT_LINE}
            disabled={false}
            defaultValue={make}
            onChange={(input: string) => onChangeMake(input)}
          />
          <InputField
            id="modelInput"
            className="border-2 border-concrete bg-pureWhite enabled:hover:bg-pureWhite text-lg w-full"
            labelClassName="text-charcoal"
            label="Modellserie"
            type={InputType.TEXT_LINE}
            disabled={false}
            defaultValue={modelSeries}
            onChange={(input: string) => onChangeModelSeries(input)}
          />
          <Dropdown
            onChange={handleChange}
            value={selectedValue}
            options={dropDownOptions()}
            placeholder="Velg type"
            label={'Bestillingstype'}
            menuClassName="w-full"
            iconProps={{
              iconDropdownDown: IconType.ChevronDown,
              iconDropdownUp: IconType.ChevronUp,
              iconColor: 'var(--color-black)',
            }}
            className="border-2 border-black"
          />
        </div>
        {errorMessage !== '' && <div className="text-sm text-poppy pt-2 text-left">{errorMessage}</div>}
        <footer className="flex gap-8 pt-16">
          <Button
            variant="teal"
            onClick={createOrder}
            disabled={isOfferBtnDisable()}
            className="px-[1.875rem] py-[0.95rem] font-bold disabled:bg-neutralGrey disabled:opacity-100 enabled:hover:bg-teal disabled:text-neutralPlus focus:ring-0"
          >
            {isLoading ? <LoadingSpinner color={'var(--color-white)'} size={24} /> : 'Bekreft'}
          </Button>
          <Button
            variant="teal"
            appearance="outline"
            onClick={onModalClose}
            className="px-[1.875rem] py-[0.95rem] font-bold border-[3px] enabled:hover:bg-turquoise"
          >
            {isLoading ? <LoadingSpinner size={24} /> : 'Avbryt'}
          </Button>
        </footer>
      </div>
    </CustomModal>
  );
};
export default CreateOrder;
